// src/modules/app-views/products/pages/Document/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { SimpleFormProvider } from 'components/SimpleComponents/SimpleFormProvider';
import useZustand from 'hooks/useZustand';
import { Tabs } from 'antd';
import translateX from 'utils/translateX';
import { useEffect, useMemo, useState } from 'react';
import { useDocumentContext } from 'components/core/DocumentProvider';
import SimpleFormProviderInitializer from 'components/SimpleComponents/SimpleFormProviderInitializer';
import SimpleLoadingArea from 'components/SimpleComponents/SimpleLoadingArea';
import { buildInitialValues } from 'utils/mock';
import useFormInstance from 'hooks/useFormInstance';
import { FormOutlined, AuditOutlined, ShopOutlined, InboxOutlined } from '@ant-design/icons';
import CardHeaderWithCountry from 'layouts/Pages/PageHeader/CardHeaderWithCountry';
import ProductsRecordTab from 'modules/app-views/products/pages/Document/tabs/RecordTab';
import ProductsFiscalTab from 'modules/app-views/products/pages/Document/tabs/FiscalTab';
import ProductsAiInfoTab from 'modules/app-views/products/pages/Document/tabs/AiInfoTab';
import ProductsSuppliersTab from 'modules/app-views/products/pages/Document/tabs/SuppliersTab';
import ProductsStockTab from 'modules/app-views/products/pages/Document/tabs/StockTab';
import aiGenerativeIcon from 'assets/images/ai-generative.png';
import { RiRobot2Line } from 'react-icons/ri';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const isMockStep = false;

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const ProductsDocument = props => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const storedSubscription = useZustand('auth', 'subscription');
  const { tab, initTab, handleTabChange, availableTabs, buildTabs, data, viewMode, registerTabFieldPrefixes } =
    useDocumentContext();

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  // Get upsert from global state - auto-fetch is handled by DocumentProvider
  const { upsert, urlInfo } = useZustand('global', 'useApi');
  const { formRef, form, isInitializedRef, excludeFieldsFromReset } = useFormInstance({ data });
  const _id = urlInfo?.params?._id;

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const requiredFields = ['name', 'unitUid', 'price'];

  const tabsItems = useMemo(() => {
    return Array.isArray(availableTabs)
      ? availableTabs
      : availableTabs
        ? Object.values(availableTabs)
        : [];
  }, [availableTabs]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  // NOTE: useMemo instead of useState to react when data becomes available
  const initialValues = useMemo(() => buildInitialValues(isMockStep, data), [data]);
  const [disabledFields, setDisabledFields] = useState([]);
  const [state, setState] = useState({
    dataOrigin: 'new_data',
  });

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────

  // Map field prefixes to tabs so validation errors navigate to the correct tab
  useEffect(() => {
    if (!initTab) return;
    registerTabFieldPrefixes?.({
      fiscalData: 'fiscal',
      stock: 'stock',
      stockLocations: 'stock',
      pricing: 'suppliers',
      '': initTab,
    });
  }, [registerTabFieldPrefixes, initTab]);

  // Build tabs
  useEffect(() => {
    const childProps = {
      ...props,
      initTab,
      state,
      setState,
      viewMode,
      data,
    };
    const itemsLocal = buildTabsArray(childProps);
    buildTabs(itemsLocal);
  }, [state, initTab, viewMode, data?.aiCatalogMeta?.catalogedByAI]); // include data for AI tab visibility

  // NOTE: savedFiscalDataRef initialization is now handled by useFiscalDataTracking hook

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleFinish = async ({ values, allValues }) => {
    try {
      const productId = values?._id || allValues?._id || data?._id;
      const dataToSave = { ...values };

      // For new documents (manual registration), explicitly set origin to 0 (manual)
      // This prevents backend from defaulting to origin: 2 (automatic)
      const isNewDocument = !_id || _id === 'add';
      if (isNewDocument) {
        dataToSave._metadata = { ...dataToSave._metadata, origin: 0 };
      }

      // Always include stockLocations from form (array diff not detected by getChangedFields)
      const formLocations = form?.getFieldValue('stockLocations');
      if (formLocations !== undefined) {
        dataToSave.stockLocations = Array.isArray(formLocations) ? formLocations : [];
      }

      await upsert({ dataSet: dataToSave, _id: productId });
    } catch (err) {
      throw err;
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <>
      <div className="container mt-3">
        <SimpleFormProvider
          formRef={formRef}
          onFinish={handleFinish}
          showSubmitButton={false}
          initFocus="sku"
          reloadEntirePage={true}
          ignoreResetFormOnSubmit={true}
          requiredFields={requiredFields}
          disabledFields={disabledFields}
          locked={viewMode === 'view'}
          showInfoMessage={true}
          // forceViewAfterSave={false}
          initialValues={initialValues}
          data={data}
        >
          <SimpleFormProviderInitializer data={data} isInitializedRef={isInitializedRef} />

          <div className="position-relative">
            <CardHeaderWithCountry
              overlapsBackground
              data={data}
              avatarName={data?.organization}
              watchField="countryCode"
            />

            <SimpleLoadingArea
              name="cardHeader"
              offsetTop={0}
              showProgress={false}
              zIndex={100}
              borderRadius={8}
            />
          </div>

          <Tabs
            type="line"
            tabPlacement="top"
            activeKey={tab}
            rootClassName="tab-in-form no-content-padding"
            items={tabsItems}
            onChange={handleTabChange}
            animated={{ inkBar: true, tabPane: false }}
            destroyOnHidden={false}
          />
        </SimpleFormProvider>
      </div>
    </>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default ProductsDocument;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function buildTabsArray(props) {
  const { initTab, viewMode, data } = props;

  const isNewDocument = viewMode === 'add';

  // Check if product was cataloged by AI
  const hasCatalogedByAi = data?.aiCatalogMeta?.catalogedByAI === true;

  return [
    {
      key: initTab,
      label: (
        <span className="tab-icon-label">
          <FormOutlined />
          {translateX('record')}
        </span>
      ),
      hide: false,
      disabled: false,
      children: <ProductsRecordTab {...props} tabName={initTab} />,
    },
    {
      key: 'fiscal',
      label: (
        <span className="tab-icon-label">
          <AuditOutlined />
          {translateX('fiscal')}
        </span>
      ),
      hide: false,
      disabled: isNewDocument,
      children: <ProductsFiscalTab {...props} tabName="fiscal" />,
    },
    {
      key: 'stock',
      label: (
        <span className="tab-icon-label">
          <InboxOutlined />
          {translateX('stock')}
        </span>
      ),
      hide: false,
      disabled: isNewDocument,
      children: <ProductsStockTab {...props} tabName="stock" />,
    },
    {
      key: 'suppliers',
      label: (
        <span className="tab-icon-label">
          <ShopOutlined />
          {translateX('cost')}
        </span>
      ),
      hide: false,
      disabled: isNewDocument,
      children: <ProductsSuppliersTab {...props} tabName="suppliers" />,
    },
    {
      key: 'aiInfo',
      label: (
        <span className="tab-icon-label">
          <RiRobot2Line />
          {translateX('ai_cataloging')}
        </span>
      ),
      hide: !hasCatalogedByAi,
      disabled: false,
      children: <ProductsAiInfoTab {...props} tabName="aiInfo" />,
    },
  ];
}
